'use client'

import Link from 'next/link'
import React from 'react'
import {DetailMetadataItem} from '@fitro/customer-ui/DetailMetadata'
import {Badge} from '@fitro/customer-ui/Badge'
import {InfoIcon} from '../../ui/icons'
import {cn} from '../../../lib/utils'
import {CLIENT_RWD_INFO_BUTTON_CLASS} from './clientRwd'

const CLIENT_SURFACE_CARD_ROOT_CLASS =
    'relative overflow-hidden rounded-[16px] border border-[rgba(15,23,42,0.08)] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_3px_12px_rgba(16,24,40,0.05)] dark:border-white/10 dark:bg-gray-900'

const CLIENT_SURFACE_CARD_CONTENT_CLASS = 'px-4 pt-4 pb-4'

export const CLIENT_SURFACE_TITLE_CLASS =
    'block min-w-0 text-left text-[15px] font-semibold leading-[1.28] tracking-[-0.02em] text-gray-900 dark:text-gray-100'

export const CLIENT_SURFACE_SUBTITLE_CLASS =
    'min-w-0 text-left text-[13px] font-medium leading-[1.35] text-gray-600 dark:text-gray-300'

export const CLIENT_SURFACE_HEADER_CLASS =
    '-mx-4 -mt-4 min-w-0 border-b border-[rgba(15,23,42,0.06)] py-1 pl-4 pr-2 dark:border-white/10'

const CLIENT_SURFACE_STATUS_BADGE_CLASS =
    '!min-h-[22px] !rounded-[8px] !px-2 !py-0.5 !text-[12px] !font-medium !leading-4'

export const CLIENT_SURFACE_META_ROW_CLASS =
    'mt-2 flex items-center gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide'

const CLIENT_SURFACE_META_PILL_CLASS =
    'inline-flex min-h-[22px] max-w-full shrink-0 items-center justify-center rounded-full bg-gray-100 px-[10px] py-[2px] text-[12px] font-medium leading-4 text-gray-500 dark:bg-gray-800 dark:text-gray-400'

export const CLIENT_SURFACE_SECTION_CLASS =
    'mt-2 flex flex-col gap-1.5'

export const CLIENT_SURFACE_PROGRESS_ROW_CLASS = 'flex items-center gap-2'
export const CLIENT_SURFACE_PROGRESS_TRACK_CLASS =
    'h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700'
export const CLIENT_SURFACE_PROGRESS_FILL_CLASS =
    'origin-left bg-primary-500 transition-transform duration-normal ease-out motion-reduce:transition-none dark:bg-primary-400'
export const CLIENT_SURFACE_PROGRESS_VALUE_CLASS =
    'shrink-0 text-[12px] font-semibold leading-4 text-gray-600 dark:text-gray-300'

export const CLIENT_SURFACE_DETAIL_LABEL_CLASS = 'font-semibold text-gray-600 dark:text-gray-300'
const CLIENT_SURFACE_DETAIL_VALUE_CLASS = 'font-medium text-gray-900 dark:text-gray-100'

interface ClientSurfaceCardProps {
    children: React.ReactNode
    className?: string
    contentClassName?: string
    style?: React.CSSProperties
}

interface ClientSurfaceStatusBadgeProps {
    children: React.ReactNode
    variant: React.ComponentProps<typeof Badge>['variant']
    className?: string
}

interface ClientSurfaceDetailLineProps {
    label: React.ReactNode
    value: React.ReactNode
    className?: string
}

interface ClientSurfaceInfoButtonProps {
    onClick: () => void
    ariaLabel: string
    className?: string
}

interface ClientSurfaceInfoLinkProps {
    href: string
    ariaLabel: string
    className?: string
}

interface ClientSurfaceMetaPillProps {
    children: React.ReactNode
    className?: string
}

interface ClientSurfaceSummaryItemProps {
    size?: 'default' | 'large'
    label: React.ReactNode
    value: React.ReactNode
    className?: string
}

const formatDetailLabel = (label: React.ReactNode) => {
    if (typeof label !== 'string') {
        return label
    }

    return label.trim().endsWith(':') ? label : `${label}:`
}

const hasRenderableDetailValue = (value: React.ReactNode): boolean => {
    if (value === null || value === undefined || value === false) return false
    if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed.length > 0 && trimmed !== '—'
    }
    if (Array.isArray(value)) {
        return value.some((item) => hasRenderableDetailValue(item))
    }
    return true
}

export const ClientSurfaceCard: React.FC<ClientSurfaceCardProps> = ({
    children,
    className,
    contentClassName,
    style
}) => (
    <div data-testid="client-surface-card" className={cn(CLIENT_SURFACE_CARD_ROOT_CLASS, className)} style={style}>
        <div className={cn(CLIENT_SURFACE_CARD_CONTENT_CLASS, contentClassName)}>{children}</div>
    </div>
)

export const ClientSurfaceStatusBadge: React.FC<ClientSurfaceStatusBadgeProps> = ({
    children,
    variant,
    className
}) => (
    <Badge variant={variant} size="small" className={cn(CLIENT_SURFACE_STATUS_BADGE_CLASS, className)}>
        {children}
    </Badge>
)

export const ClientSurfaceMetaPill: React.FC<ClientSurfaceMetaPillProps> = ({children, className}) => (
    <span className={cn(CLIENT_SURFACE_META_PILL_CLASS, className)}>
        {children}
    </span>
)

export const ClientSurfaceSummaryGrid: React.FC<{children: React.ReactNode; className?: string}> = ({
    children,
    className
}) => (
    <div className={cn('mt-3 grid grid-cols-2 gap-x-3 gap-y-3', className)}>
        {children}
    </div>
)

export const ClientSurfaceSummaryItem: React.FC<ClientSurfaceSummaryItemProps> = ({
    size = 'default',
    label,
    value,
    className
}) => {
    if (!hasRenderableDetailValue(value)) return null
    if (size === 'large') return <DetailMetadataItem label={label} value={value} className={className}/>

    return (
        <div className={cn('flex min-w-0 flex-col items-center text-center', className)}>
            <div className={cn("max-w-full font-medium text-gray-500 dark:text-gray-400", "truncate text-[12px] leading-4")}>
                {label}
            </div>
            <div className={cn("mt-1 max-w-full font-semibold text-gray-900 dark:text-gray-100", "truncate text-[13px] leading-5")}>
                {value}
            </div>
        </div>
    )
}

export const ClientSurfaceDetailLine: React.FC<ClientSurfaceDetailLineProps> = ({
    label,
    value,
    className
}) => {
    if (!hasRenderableDetailValue(value)) {
        return null
    }

    return (
        <div className={cn('min-w-0 overflow-x-auto whitespace-nowrap scrollbar-hide text-[14px] leading-[18px]', className)}>
            <span className={CLIENT_SURFACE_DETAIL_LABEL_CLASS}>{formatDetailLabel(label)}</span>{' '}
            <span className={CLIENT_SURFACE_DETAIL_VALUE_CLASS}>{value}</span>
        </div>
    )
}

export const ClientSurfaceInfoButton: React.FC<ClientSurfaceInfoButtonProps> = ({
    onClick,
    ariaLabel,
    className
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(CLIENT_RWD_INFO_BUTTON_CLASS, className)}
    >
        <InfoIcon className="h-5 w-5"/>
    </button>
)

export const ClientSurfaceInfoLink: React.FC<ClientSurfaceInfoLinkProps> = ({
    href,
    ariaLabel,
    className
}) => (
    <Link
        href={href}
        aria-label={ariaLabel}
        className={cn(CLIENT_RWD_INFO_BUTTON_CLASS, className)}
    >
        <InfoIcon className="h-5 w-5"/>
    </Link>
)

ClientSurfaceCard.displayName = 'ClientSurfaceCard'
ClientSurfaceStatusBadge.displayName = 'ClientSurfaceStatusBadge'
ClientSurfaceMetaPill.displayName = 'ClientSurfaceMetaPill'
ClientSurfaceSummaryGrid.displayName = 'ClientSurfaceSummaryGrid'
ClientSurfaceSummaryItem.displayName = 'ClientSurfaceSummaryItem'
ClientSurfaceDetailLine.displayName = 'ClientSurfaceDetailLine'
ClientSurfaceInfoButton.displayName = 'ClientSurfaceInfoButton'
ClientSurfaceInfoLink.displayName = 'ClientSurfaceInfoLink'
